import { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import Button from "../Common/Button";
import { ENERGY_UNIT, formatConvertedEnergyValue } from "../../utils/energyUnit";


const DualAxisLineChart = ({
  data,
  energyUnit = ENERGY_UNIT.KWH,
  energyUnitLabel = "kWh",
  "data-testid": dataTestId = "dual-axis-line-chart",
}) => {
  const [showOutdoorTemperature, setShowOutdoorTemperature] = useState(true);


  const CustomLegend = () => {
    return (
      <div className="flex gap-4 items-center justify-center px-4 py-2 min-h-8 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={() => setShowOutdoorTemperature((prev) => !prev)}
            aria-pressed={showOutdoorTemperature}
            aria-label={`${showOutdoorTemperature ? "Hide" : "Show"} Outdoor Temperature`}
            data-testid={`${dataTestId}-toggle-temperature-button`}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
              showOutdoorTemperature
                ? "bg-[#FF8C65]"
                : "bg-gray-300 dark:bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 ${
                showOutdoorTemperature ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </Button>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full border-2 ${showOutdoorTemperature ? "border-[#FF8C65]" : "border-gray-300 dark:border-gray-600"}`} />
            <span className={`text-xs ${showOutdoorTemperature ? "text-gray-700 dark:text-white" : "text-gray-400 dark:text-gray-500"}`}>
              Outdoor Temperature (°F)
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full border-2 border-[#1E90FF]" />
          <span className="text-xs text-gray-700 dark:text-white">Aggregate Energy Usage ({energyUnitLabel})</span>
        </div>
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="rounded-lg px-4 py-3 shadow-md text-xs font-semibold text-white bg-[#1E555C]">
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((item, index) => (
          <div key={index} className="flex justify-between gap-2">
            <span>{item.name}</span>
            <span>
              {item.name.includes("Temperature")
                ? item.value
                : formatConvertedEnergyValue(item.value, energyUnit)}
              {item.name.includes("Temperature") ? " °F" : ` ${energyUnitLabel}`}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const [date, time] = payload.value.split("|");
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={8} dy={0} textAnchor="middle" className="text-[#666] dark:text-white" fill="currentcolor" fontSize="12">
          {date}
        </text>
        <text x={0} y={20} textAnchor="middle" className="text-[#666] dark:text-white" fill="currentcolor" fontSize="10">
          ({time})
        </text>
      </g>
    );
  };

  const maxEnergyUsage = Math.max(...data.map((d) => parseFloat(d.energyUsage || 0)));
  const minEnergyUsage = Math.min(...data.map((d) => parseFloat(d.energyUsage || 0)));
  const energyRange = maxEnergyUsage - minEnergyUsage;
  const energyPadding = energyRange > 0 ? Math.max(0.1, energyRange * 0.15) : 0.5;
  const energyDomainMin = Math.max(0, Number((minEnergyUsage - energyPadding).toFixed(2)));
  const energyDomainMax = Number((maxEnergyUsage + energyPadding).toFixed(2));

  const maxTemp = Math.max(...data.map(d => parseFloat(d.temperature || 0)))
  const TemperatureDomainMax = Math.ceil(maxTemp + 2);

  const minTemp = Math.min(...data.map(d => parseFloat(d.temperature || 0)));
  const temperatureDomainMin = Math.floor(minTemp - 2);

  return (
    <div className="w-full min-w-0 h-[358px]" data-testid={dataTestId}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="dateTime" tick={<CustomXAxisTick />} tickLine={false} axisLine={false} />
          {showOutdoorTemperature && (
            <YAxis
              yAxisId="left"
              tick={{ className: "text-[#555] dark:text-white", fontSize: 12, fill: "currentcolor" }}
              domain={[temperatureDomainMin, TemperatureDomainMax]}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => Math.round(value)}
              label={{
                className: "text-[#555] dark:text-white",
                value: "Temperature (in °F)",
                angle: -90,
                position: "insideLeft",
                offset: 10,
                style: { textAnchor: "middle", fill: "currentcolor", fontSize: 12 },
              }}
            />
          )}
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ className: "text-[#555] dark:text-white", fontSize: 12, fill: "currentcolor" }}
            tickLine={false}
            axisLine={false}
            domain={[energyDomainMin, energyDomainMax]}
            tickFormatter={(value) => formatConvertedEnergyValue(value, energyUnit)}
            label={{
              className: "text-[#555] dark:text-white",
              value: `Energy Usage (in ${energyUnitLabel})`,
              angle: 90,
              position: "insideRight",
              offset: 10,
              style: { textAnchor: "middle", fill: "currentcolor", fontSize: 12 },
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
          {showOutdoorTemperature && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="temperature"
              name="Outdoor Temperature (°F)"
              stroke="#FF8C65"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 6 }}
            />
          )}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="energyUsage"
            name={`Aggregate Energy Usage (${energyUnitLabel})`}
            stroke="#1E90FF"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DualAxisLineChart;
